class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const root = this; // <predictive-search>

    // Core elements
    const formElement = root.querySelector("#ps-form");
    const inputElement = root.querySelector("#ps-input");
    const popoverElement = root.querySelector("#ps-popover");
    const clearButtonInsideInput = root.querySelector(".ps-clear-input");
    const closeBtn = root.querySelector('.ps-clear-btn');

    // Recent searches elements (optional)
    const recentContainer = root.querySelector("[data-recent-searches]");
    const recentListElement = root.querySelector("[data-recent-searches-list]");
    const clearRecentButton = root.querySelector(
      "[data-recent-searches-clear]"
    );

    // Recently viewed elements (optional)
    const recentlyViewedContainer = root.querySelector(
      "[data-recently-viewed]"
    );
    const recentlyViewedList = root.querySelector(
      "[data-recently-viewed-list]"
    );
    const clearRecentViewedButton = root.querySelector(
      "[data-recent-viewed-clear]"
    );

    if (
      !formElement ||
      !inputElement ||
      !popoverElement ||
      !clearButtonInsideInput
    ) {
      return;
    }

    // Configuration
    const MIN_QUERY_LENGTH = 2;
    const MAX_RECENT_SEARCHES = 4;
    const STORAGE_KEY_RECENT_SEARCHES = "ps_recent_searches";

    const STORAGE_KEY_RECENTLY_VIEWED = "ps_recently_viewed";
    const MAX_RECENTLY_VIEWED = 8;

    const getRecentlyViewedProducts = () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY_RECENTLY_VIEWED);
        return raw ? JSON.parse(raw) : [];
      } catch {
        return [];
      }
    };

    const saveRecentlyViewedProducts = (items) => {
      localStorage.setItem(STORAGE_KEY_RECENTLY_VIEWED, JSON.stringify(items));
    };

    const baseUrl =
      inputElement.dataset.predictiveSearchUrl || "/search/suggest.json";
    const sectionId = inputElement.dataset.sectionId || "predictive-search";

    let activeOptionIndex = -1;
    let currentOptions = [];

    //  Helpers
    const debounce = (fn, delay = 180) => {
      let timeoutId;
      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
      };
    };

    const buildSuggestUrl = (query) => {
      const params = new URLSearchParams();
      params.set("q", query);
      params.set("resources[type]", "product,collection");
      params.set("resources[limit]", "8");
      params.set("section_id", sectionId);
      return `${baseUrl}?${params.toString()}`;
    };

    const setPopoverActiveOption = (index) => {
      if (!currentOptions.length) return;

      currentOptions.forEach((optionElement) => {
        optionElement.classList.remove("is-active");
        optionElement.setAttribute("aria-selected", "false");
      });

      if (index >= 0 && index < currentOptions.length) {
        const optionElement = currentOptions[index];
        optionElement.classList.add("is-active");
        optionElement.setAttribute("aria-selected", "true");
        optionElement.scrollIntoView({ block: "nearest" });
      }

      activeOptionIndex = index;
    };

    const showPopover = () => {      
      popoverElement.hidden = false;
      inputElement.setAttribute("aria-expanded", "true");
      clearButtonInsideInput.hidden = !inputElement.value?.length;
    };

    const hidePopover = () => {
      popoverElement.hidden = true;
      inputElement.setAttribute("aria-expanded", "false");
      activeOptionIndex = -1;
      currentOptions = [];
    };

    const openSearch = () => {
      root.classList.add("is-open");      
      if(document.body.dataset.template === 'index') {
        closeBtn.classList.remove('hidden');
      }
      inputElement.setAttribute("aria-expanded", "true");
      document.body.classList.add("overflow-hidden");
    };

    const renderPopoverHtml = (html) => {
      popoverElement.innerHTML = html;
      currentOptions = Array.from(
        popoverElement.querySelectorAll('[role="option"]')
      );
      showPopover();
    };

    // Recent searches logic (session storage)

    const getRecentSearches = () => {
      if (!recentContainer || !recentListElement) return [];
      try {
        return (
          JSON.parse(sessionStorage.getItem(STORAGE_KEY_RECENT_SEARCHES)) || []
        );
      } catch (error) {
        return [];
      }
    };

    const saveRecentSearches = (terms) => {
      if (!recentContainer || !recentListElement) return;
      sessionStorage.setItem(
        STORAGE_KEY_RECENT_SEARCHES,
        JSON.stringify(terms)
      );
    };

    const renderRecentSearches = () => {
      if (!recentContainer || !recentListElement) return;

      const terms = getRecentSearches();
      recentListElement.innerHTML = "";

      if (!terms.length) {
        recentContainer.hidden = true;
        return;
      }

      terms.forEach((term) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "recent-search-chip body-xs";
        button.textContent = term.toUpperCase();

        button.addEventListener("click", () => {
          inputElement.value = term;
          formElement.submit(); // full search on click
        });

        recentListElement.appendChild(button);
      });

      recentContainer.hidden = false;
    };

    const renderRecentlyViewed = () => {
      if (!recentlyViewedContainer || !recentlyViewedList) return;

      const items = getRecentlyViewedProducts();
      recentlyViewedList.innerHTML = "";

      if (!items.length) {
        recentlyViewedContainer.hidden = true;
        return;
      }

      const fragment = document.createDocumentFragment();

      items.forEach((product) => {
        const a = document.createElement("a");
        a.href = product.url;
        a.className = "recently-viewed-thumb";

        const img = document.createElement("img");
        img.src = product.image;
        img.loading = "lazy";
        img.decoding = "async";

        a.appendChild(img);
        fragment.appendChild(a);
      });

      recentlyViewedList.appendChild(fragment);
      recentlyViewedContainer.hidden = false;
    };

    const addRecentSearch = (term) => {
      if (!term || !term.trim()) return;
      if (!recentContainer || !recentListElement) return;

      const normalizedTerm = term.trim();
      let terms = getRecentSearches();

      const existingIndex = terms.findIndex(
        (storedTerm) =>
          storedTerm.toLowerCase() === normalizedTerm.toLowerCase()
      );
      if (existingIndex !== -1) {
        terms.splice(existingIndex, 1);
      }

      terms.unshift(normalizedTerm);
      if (terms.length > MAX_RECENT_SEARCHES) {
        terms = terms.slice(0, MAX_RECENT_SEARCHES);
      }

      saveRecentSearches(terms);
      renderRecentSearches();
    };

    const clearAllRecentSearches = () => {
      saveRecentSearches([]);
      renderRecentSearches();
    };

    const clearAllRecentViwed = () => {
      saveRecentlyViewedProducts([]);
      renderRecentlyViewed();
    };

    // Predictive search logic

    const requestSuggestions = async () => {
      const query = inputElement.value.trim();

      if (query.length < MIN_QUERY_LENGTH) {
        hidePopover();
        return;
      }

      try {
        const response = await fetch(buildSuggestUrl(query), {
          headers: { "X-Requested-With": "XMLHttpRequest" },
        });
        const html = await response.text();
        renderPopoverHtml(html);
        setPopoverActiveOption(-1);
      } catch (error) {
        hidePopover();
      }
    };

    const handleInputKeyDown = (event) => {
      if (popoverElement.hidden || !currentOptions.length) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setPopoverActiveOption(
            Math.min(activeOptionIndex + 1, currentOptions.length - 1)
          );
          break;
        case "ArrowUp":
          event.preventDefault();
          setPopoverActiveOption(Math.max(activeOptionIndex - 1, 0));
          break;
        case "Enter":
          if (activeOptionIndex >= 0 && currentOptions[activeOptionIndex]) {
            event.preventDefault();
            const link =
              currentOptions[activeOptionIndex].querySelector("a[href]");
            if (link) window.location.href = link.href;
          }
          break;
        case "Escape":
          hidePopover();
          inputElement.blur();
          break;
      }
    };

    //events

    const debouncedRequestSuggestions = debounce(requestSuggestions, 160);

    // Open search on focus
    inputElement.addEventListener("focus", () => {
      renderRecentlyViewed();
      renderRecentSearches();
      openSearch();
      this.classList.add("is-open");

      const hasQuery = inputElement.value.trim().length >= MIN_QUERY_LENGTH;
      if (hasQuery && popoverElement.innerHTML.trim()) {
        showPopover();
      }
    });

    // Input changes
    inputElement.addEventListener("input", () => {
      debouncedRequestSuggestions();
      if (inputElement.value.trim() != "") {
        if (clearButtonInsideInput) {
          clearButtonInsideInput.classList.remove("hidden");
        }
      } else {
        if (clearButtonInsideInput) {
          clearButtonInsideInput.classList.add("hidden");
        }
      }
    });

    inputElement.addEventListener("keydown", handleInputKeyDown);

    // Hover / active state inside popover
    popoverElement.addEventListener("mousemove", (event) => {
      const optionElement = event.target.closest('[role="option"]');
      if (!optionElement) return;

      const index = currentOptions.indexOf(optionElement);
      if (index >= 0) setPopoverActiveOption(index);
    });

    popoverElement.addEventListener("mouseleave", () =>
      setPopoverActiveOption(-1)
    );

    // Click on a suggestion
    popoverElement.addEventListener("click", (event) => {
      const link = event.target.closest("a[href]");
      if (!link) return;

      event.preventDefault();
      window.location.href = link.href;
    });

    // Clear search input
    if (clearButtonInsideInput) {
      clearButtonInsideInput.addEventListener("click", () => {
        inputElement.value = "";
        clearButtonInsideInput.classList.add("hidden");

        inputElement.focus();
        popoverElement.hidden = true;        
      });
    }

    // Full search submit + store recent term
    formElement.addEventListener("submit", () => {
      hidePopover();
      addRecentSearch(inputElement.value);
    });

    // Clear all recent searches
    if (clearRecentButton) {
      clearRecentButton.addEventListener("click", clearAllRecentSearches);
    }

    // Clear all recent searches
    if (clearRecentViewedButton) {
      clearRecentViewedButton.addEventListener("click", clearAllRecentViwed);
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);
